import React, { useEffect, useState } from 'react'
import clone from 'lodash/clone'
import { useTranslation } from 'react-i18next'

import MaterialCategory from '@components/blocks/components/material_category'
import MaterialFilter from '@components/blocks/components/material_filter'
import MaterialSet from '@components/blocks/components/material_set'
import Tab from '@components/tab'
import contentfulMaterialTagsQuery from '@queries/materialTagsQuery'
import { materialsByTags } from '@util/materials'
import styles from '@components/blocks/printable_materials.module.scss'
import ImageAsset from '@components/image_asset'

const PrintableMaterials = React.forwardRef(
  (
    {
      fields: {
        image,
        title,
        guidesTitle,
        vroomTipSheetsTitle,
        promotionalPiecesTitle,
        guidesSubtitle,
        vroomTipSheetsSubtitle,
        promotionalPiecesSubtitle,
        contentGuidelinesAgreement,
        guidesMaterialSets,
        vroomTipSheetsMaterialSets,
        promotionalPiecesMaterialSets,
        contentAreasLabel,
      },
      materialTagsQuery = contentfulMaterialTagsQuery,
    },
    _
  ) => {
    const { t } = useTranslation()
    const [selectedTab, setSelectedTab] = useState(guidesTitle)
    const [materialFilters, setMaterialFilters] = useState({})
    const [filteredMaterials, setFilteredMaterials] = useState({})

    useEffect(() => {
      if (Object.keys(materialFilters).length === 0) {
        return
      }

      let tagIds = []
      Object.values(materialFilters).forEach(categoryTagIds => {
        tagIds = tagIds.concat(categoryTagIds)
      })

      const newFilteredMaterials = materialsByTags(tagIds, vroomTipSheetsMaterialSets)
      setFilteredMaterials(newFilteredMaterials)
    }, [materialFilters, vroomTipSheetsMaterialSets])

    useEffect(() => {
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search)
        const tabParam = params.get('tab')
        if (tabParam) {
          setSelectedTab(tabParam)
        }
      }
    }, [])

    const onTabSelect = tab => {
      setSelectedTab(tab)
      setMaterialFilters({})
    }

    const tabsContent = {
      [guidesTitle]: {
        subtitle: guidesSubtitle,
        materialSets: guidesMaterialSets,
        color: 'blue',
      },
      [vroomTipSheetsTitle]: {
        subtitle: vroomTipSheetsSubtitle,
        materialSets: vroomTipSheetsMaterialSets,
        color: 'teal',
      },
      [promotionalPiecesTitle]: {
        subtitle: promotionalPiecesSubtitle,
        materialSets: promotionalPiecesMaterialSets,
        color: 'purple',
      },
    }

    const selectedTabContent = tabsContent[selectedTab]

    const onFilterSelected = (filter, selectedOptions) => {
      const newMaterialFilters = clone(materialFilters)

      if (selectedOptions.length === 0) {
        delete newMaterialFilters[filter]
      } else {
        newMaterialFilters[filter] = selectedOptions.map(option => option.id)
      }

      setMaterialFilters(newMaterialFilters)
    }

    return (
      <div id="PrintableMaterials" className={styles.printable_materials}>
        <div className={styles.printable_materials__title_container}>
          <h2 className={styles.printable_materials__title}>{title}</h2>
          {image && (
            <ImageAsset
              asset={image}
              width="137"
              height="134"
              className={styles.printable_materials__title_image}
            />
          )}
        </div>
        <div className={styles.printable_materials__tabs_container}>
          <Tab onTabSelect={onTabSelect} selectedTab={selectedTab} title={guidesTitle} />
          <Tab onTabSelect={onTabSelect} selectedTab={selectedTab} title={vroomTipSheetsTitle} />
          <Tab onTabSelect={onTabSelect} selectedTab={selectedTab} title={promotionalPiecesTitle} />
        </div>
        <div className={styles.printable_materials__subtitle_container}>
          <h2 className={styles.printable_materials__subtitle}>{selectedTabContent.subtitle}</h2>
        </div>
        {selectedTab === vroomTipSheetsTitle && (
          <div className={styles.printable_materials__filters}>
            <MaterialFilter
              title={t('contentAreas')}
              materialTagCategory="Content Area"
              onFilterSelected={onFilterSelected}
              materialTagsQuery={materialTagsQuery}
            />
            <MaterialFilter
              title={t('language')}
              materialTagCategory="Language"
              onFilterSelected={onFilterSelected}
              materialTagsQuery={materialTagsQuery}
            />
            <MaterialFilter
              title={t('age')}
              materialTagCategory="Age"
              onFilterSelected={onFilterSelected}
              materialTagsQuery={materialTagsQuery}
            />
          </div>
        )}
        <div className={styles.printable_materials__sets}>
          {Object.keys(materialFilters).length === 0 &&
            selectedTabContent.materialSets.map(materialSet => (
              <MaterialSet
                key={materialSet.id}
                fields={materialSet.fields}
                color={selectedTabContent.color}
                contentGuidelinesAgreement={contentGuidelinesAgreement}
                contentAreasLabel={contentAreasLabel}
              />
            ))}
          {Object.keys(materialFilters).length > 0 &&
            Object.entries(filteredMaterials).map(([id, category]) => (
              <MaterialCategory
                key={id}
                type={category.setType}
                description={category.setDescription}
                displayText={category.categoryDisplayText}
                materials={category.materials}
                color={selectedTabContent.color}
                contentGuidelinesAgreement={contentGuidelinesAgreement}
              />
            ))}
        </div>
      </div>
    )
  }
)

export default PrintableMaterials
